/**
 * Invitación del perito y sus varias empresas (tanda 5, 26-09-2026).
 *
 * Un solo sitio para hablar con la base de datos de esto (las funciones de la
 * migración 20260926160000) y para las reglas de pantalla que tienen que decir lo
 * mismo que el servidor: cómo se limpia un código pegado de WhatsApp, qué texto
 * corresponde a cada error y cómo se arma el enlace que se comparte.
 *
 * Las RPC nuevas no están en `types.ts` (se regenera al aplicar la migración): se
 * llaman a través de `rpc()` de abajo, tipadas aquí, para no tocar el fichero
 * generado ni repartir `as any` por las pantallas.
 */
import { supabase } from "@/integrations/supabase/client";

export * from "./peritoInvitacionReglas";

/* ───────────────────────── llamadas al servidor ───────────────────────── */

export interface InvitacionPendiente {
  id: string;
  code: string | null;
  token: string | null;
  created_at: string | null;
  expires_at: string | null;
  created_by_name?: string | null;
}

export interface PeritoDeLaEmpresa {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
  linked_at: string | null;
  is_here: boolean;
  other_companies: number;
}

export interface ResultadoDeCanje {
  status: "ok" | "error";
  error?: string;
  company_id?: string;
  company_name?: string | null;
  expires_at?: string | null;
  moved_to?: string;
}

type RespuestaRpc<T> = { data: T | null; error: { message: string; code?: string } | null };

/* Las RPC nuevas no están en los tipos generados: una sola conversión, aquí. */
function rpc<T>(nombre: string, args: Record<string, unknown>): Promise<RespuestaRpc<T>> {
  const llamar = supabase.rpc as unknown as (n: string, a: Record<string, unknown>) => Promise<RespuestaRpc<T>>;
  return llamar.call(supabase, nombre, args);
}

/** ¿La función no existe todavía en la base (migración sin aplicar)? */
export function esFuncionInexistente(error: { code?: string; message?: string } | null | undefined): boolean {
  if (!error) return false;
  return error.code === "PGRST202" || error.code === "42883" || /Could not find the function/i.test(error.message ?? "");
}

export const invitaciones = {
  crear: (companyId: string) => rpc<InvitacionPendiente & { reused: boolean }>("create_perito_invite", { p_company_id: companyId }),
  /** Camino antiguo (solo devuelve el código): para cuando la migración aún no está. */
  crearAntiguo: (companyId: string) => rpc<string>("generate_perito_invite", { p_company_id: companyId }),
  pendientes: (companyId: string) => rpc<InvitacionPendiente[]>("list_perito_invites", { p_company_id: companyId }),
  anular: (id: string) => rpc<ResultadoDeCanje>("revoke_perito_invite", { p_invite_id: id }),
  canjearCodigo: (codigo: string) => rpc<ResultadoDeCanje>("link_perito_by_code", { p_code: codigo }),
  /** El enlace (token) canjeado con sesión: el perito sin empresa lo pega en su pantalla (P3). */
  canjearEnlace: (token: string) => rpc<ResultadoDeCanje>("link_perito_by_token", { p_token: token }),
  /** ¿Esta cuenta es un perito que se quedó sin empresa? (P3, migración 20260926190100) */
  soySinEmpresa: () => rpc<boolean>("perito_sin_empresa", {}),
  /** Miembros de mi empresa que están también en otra: «Quitar», no «Eliminar» (P4). */
  miembrosEnOtraEmpresa: (companyId: string) =>
    rpc<Array<{ user_id: string }>>("team_members_elsewhere", { p_company_id: companyId }),
  peritosDeLaEmpresa: (companyId: string) => rpc<PeritoDeLaEmpresa[]>("list_company_peritos", { p_company_id: companyId }),
  desvincular: (userId: string, companyId: string) =>
    rpc<ResultadoDeCanje>("unlink_perito", { p_user_id: userId, p_company_id: companyId }),
  salirme: (companyId: string) => rpc<ResultadoDeCanje>("perito_leave_company", { p_company_id: companyId }),
};

/** La edge pública del enlace. Devuelve siempre el cuerpo (también en 4xx). */
export async function llamarEnlace(
  cuerpo: Record<string, unknown>,
  accessToken?: string | null,
): Promise<ResultadoDeCanje> {
  const base = import.meta.env.VITE_SUPABASE_URL as string;
  const clave = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
  try {
    const r = await fetch(`${base}/functions/v1/accept-perito-invite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: clave,
        Authorization: `Bearer ${accessToken || clave}`,
      },
      body: JSON.stringify(cuerpo),
    });
    const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
    if (r.ok && j.status === "ok") return j as unknown as ResultadoDeCanje;
    return { status: "error", error: (j.error as string) || `HTTP_${r.status}`, company_name: (j.company_name as string) ?? null };
  } catch {
    return { status: "error", error: "NETWORK" };
  }
}
